/**
 * Page Order History
 */
import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useStylesOrderHistory as useStyles } from './styles';
import { ArrowLeft, ArrowRight } from '@material-ui/icons';
import JapaneseHolidays from 'japanese-holidays';
import ExcelJS from 'exceljs';

// Services
import ShopAuthService from 'js/shop/shop-auth-service';
import ShopOrderApiService from 'js/shop-order/shop-order-api-service';

import ShopInfoContext from 'js/shop/components/ShopInfoContext';

// Base Components
import PageContainer from 'js/shared-order/components/PageContainer';
import HeaderAppBar from 'js/shop-order/components/HeaderAppBar';
import PageInnerContainer from 'js/shared/components/PageInnerContainer';
import CustomSelectorBase from 'js/shared/components/CustomSelectorBase';
import ModalSummaryGraph from './ModalSummaryGraph';
import Waiting from 'js/shared/components/Waiting';
import FlashMessage from 'js/shared-order/components/FlashMessage';
import { ORDERGROUP_SUMMARY_MODE } from 'js/utils/helpers/const';

// Components(Material-UI)
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Box,
  Button,
  IconButton,
  Input,
} from '@material-ui/core';

import moment from 'moment';
moment.locale('ja');

const PageOrderHistory = (props) => {
  const classes = useStyles(props);
  const history = useHistory();
  const search = window.location.search;
  const params = new URLSearchParams(search);

  const tableHeadCells = [
    { id: 'date', label: '日付' },
    { id: 'total_billing', label: '売上' },
    { id: 'total_ordergroups', label: 'QR数' },
    { id: 'ordergroups_unit_price', label: '会計単価' },
    { id: 'total_number_of_customers', label: '客数' },
    { id: 'customers_unit_price', label: '客単価' },
    { id: 'total_item_count', label: '商品数' },
    {
      id: 'payment_method',
      label: '支払方法',
      colSpan: 2,
      children: [
        { id: 'cash', label: '現金' },
        { id: 'others', label: 'その他' },
      ],
    },
    { id: 'detail', label: '詳細' },
  ];
  const tableTypes = [
    { value: ORDERGROUP_SUMMARY_MODE.DAILY.NAME, label: ORDERGROUP_SUMMARY_MODE.DAILY.LABEL },
    { value: ORDERGROUP_SUMMARY_MODE.HOURLY.NAME, label: ORDERGROUP_SUMMARY_MODE.HOURLY.LABEL },
  ];

  const minDate = moment('2022-05').startOf('month');
  const maxDate = moment().endOf('month');

  const [shop] = useContext(ShopInfoContext);
  const authUser = ShopAuthService.getUserAuthStatus();

  const [waiting, setWaiting] = useState(false);
  const [toast, setToast] = useState({
    isShow: false,
    message: '',
    status: 'success',
  });
  const [showModalGraph, setShowModalGraph] = useState(false);
  const [orderGroupSummany, setOrderGroupSummany] = useState([]);
  const [filter, setFilter] = useState({
    tableType: params.get('tableType') && params.get('tableType') == 'hourly' ? 'hourly' : 'daily',
    dateMonth: moment(params.get('dateMonth') ?? moment()).format('YYYY-MM'),
    dateDay: moment(params.get('dateDay') ?? moment()).format('YYYY-MM-DD'),
  });

  useEffect(() => {
    if (shop.hashId && authUser.statusCode > 0) {
      parameterToUrl();
      getOrderHistorySummaries(filter, shop);
    }
  }, [shop, authUser, filter]);

  const fetchOrderGroupReportActual = async (filterData = {}) => {
    const params = {
      mode: filterData.tableType ?? filter.tableType,
    };

    if (params.mode === ORDERGROUP_SUMMARY_MODE.DAILY.NAME) {
      params.target_month = moment(filterData.dateMonth ?? filter.dateMonth).format('YYYY-MM');
    } else {
      params.target_date = moment(filterData.dateDay ?? filter.dateDay).format('YYYY-MM-DD');
    }

    return await ShopOrderApiService.fetchOrderGroupReportActual(shop.hashId, params);
  };

  const fetchOrderGroupReportForToday = async (filterData = {}) => {
    const params = {
      mode: filterData.tableType ?? filter.tableType,
    };

    return await ShopOrderApiService.fetchOrderGroupReportToday(shop.hashId, params);
  };

  const getOrderHistorySummaries = async (filterData = {}, shop = {}) => {
    setWaiting(true);
    let response = [];

    try {
      //過去のデータを取得する
      const actual = await fetchOrderGroupReportActual(filterData);
      //本日のデータを取得する
      const today = await fetchOrderGroupReportForToday(filterData);
      //過去のデータと本日のデータをマージする
      response = [...actual, ...today];
    } catch (error) {
      setToast({
        isShow: true,
        status: 'error',
        message: error.message,
      });
    }

    const dataConfig = {};
    const mode = filterData.tableType ?? filter.tableType;
    if (mode === ORDERGROUP_SUMMARY_MODE.DAILY.NAME) {
      const target_month = moment(filterData.dateMonth ?? filter.dateMonth).format('YYYY-MM');
      dataConfig.start = moment(target_month).startOf('month');
      dataConfig.end = dataConfig.start.clone().add(1, 'M');
      dataConfig.sameUnit = 'day';
      dataConfig.stepUnit = 'days';
    } else {
      const target_date = moment(filterData.dateDay ?? filter.dateDay).format('YYYY-MM-DD');
      const { startTime, endTime } = getBusinessHours(shop, target_date);
      dataConfig.start = startTime;
      dataConfig.end = endTime;
      dataConfig.sameUnit = 'hour';
      dataConfig.stepUnit = 'hours';
    }

    const summary = [];

    while (!dataConfig.start.isSame(dataConfig.end, dataConfig.sameUnit)) {
      const responseData =
        response.find(
          (i) =>
            dataConfig.start.isSame(i.date, 'day') &&
            i.mode === mode &&
            (i.mode === ORDERGROUP_SUMMARY_MODE.HOURLY.NAME
              ? dataConfig.start.format('HH:mm:ss') === i.hour
              : true)
        ) ?? {};
      summary.push({
        date: dataConfig.start.clone(),
        isToday: dataConfig.start.isSame(moment(), 'day'),
        isHoliday:
          dataConfig.start.weekday() === 0 ||
          JapaneseHolidays.isHoliday(new Date(dataConfig.start.format('YYYY-MM-DD'))),
        isSaturday: dataConfig.start.weekday() === 6,
        totalBilling: responseData.total_billing ?? 0,
        totalOrdergroups: responseData.total_ordergroups ?? 0,
        ordergroupsUnitPrice: responseData.ordergroups_unit_price ?? 0,
        totalNumberOfCustomers: responseData.total_number_of_customers ?? 0,
        customersUnitPrice: responseData.customers_unit_price ?? 0,
        totalItemCount: responseData.total_item_count ?? 0,
        paymentMethodCash: responseData.payment_method?.cash ?? 0,
        paymentMethodOthers: responseData.payment_method?.others ?? 0,
      });
      dataConfig.start.add(1, dataConfig.stepUnit);
    }
    setOrderGroupSummany(summary);
    setWaiting(false);
  };

  const getBusinessHours = (shop, target_date) => {
    const bussinessHours = {};
    // start timeは店舗の開始時間に設定
    bussinessHours.start = moment(target_date + ' ' + shop.start_time);
    //会計の集計時間は営業時間の開始～次の営業時間の開始まで24h分を集計する
    //end timeは翌日の開始時間に設定
    bussinessHours.end = bussinessHours.start.clone().add(1, 'day');
    //開始時間が0分以外の場合は時間を切り捨てにする
    if (bussinessHours.start.minute() > 0) {
      bussinessHours.start.startOf('hour');
    }
    // 終了時間が0分以外の場合は時間を切り上げにする
    if (bussinessHours.end.minute() > 0) {
      bussinessHours.end.startOf('hour').add(1, 'hour');
    }
    return {
      startTime: bussinessHours.start,
      endTime: bussinessHours.end,
    };
  };

  const parameterToUrl = (params = {}) => {
    const parameters = { ...filter, ...params };
    history.push({ search: '?' + new URLSearchParams(parameters).toString() });
  };

  const dateDayChange = (date, tableType) => {
    const inputDate = moment(date);
    const params = {
      ...filter,
      dateMonth: inputDate.format('YYYY-MM'),
      dateDay: inputDate.format('YYYY-MM-DD'),
    };
    if (tableType) {
      params.tableType = tableType;
    }
    setFilter(params);
  };

  const filterDateStep = (prev = false) => {
    if (filter.tableType === 'daily') {
      const date = moment(filter.dateMonth);
      const targetDate = prev ? date.subtract(1, 'M') : date.add(1, 'M');
      const dateValue = normalizeDate(targetDate);
      setFilter({
        ...filter,
        dateMonth: dateValue.format('YYYY-MM'),
        dateDay: dateValue.format('YYYY-MM-DD'),
      });
    } else if (filter.tableType === 'hourly') {
      const date = moment(filter.dateDay);
      const targetDate = prev ? date.subtract(1, 'd') : date.add(1, 'd');
      const dateValue = normalizeDate(targetDate);
      dateDayChange(dateValue.format('YYYY-MM-DD'));
    }
  };

  const normalizeDate = (date) => {
    if (date.isSameOrAfter(maxDate)) {
      return maxDate;
    }
    if (date.isSameOrBefore(minDate)) {
      return minDate;
    }
    return date;
  };

  const disablePreviousButton = () => {
    if (filter.tableType === 'daily') {
      const date = moment(filter.dateMonth);
      return date.isSameOrBefore(minDate.startOf('month'));
    } else if (filter.tableType === 'hourly') {
      const date = moment(filter.dateDay);
      return date.isSameOrBefore(minDate.startOf('day'));
    }
  };

  const disableNextButton = () => {
    if (filter.tableType === 'daily') {
      const date = moment(filter.dateMonth);
      return date.isSameOrAfter(maxDate.startOf('month'));
    } else if (filter.tableType === 'hourly') {
      const date = moment(filter.dateDay);
      return date.isSameOrAfter(maxDate.startOf('day'));
    }
  };

  const handleTableTypeChange = (event) => {
    setFilter({ ...filter, tableType: event.target.value });
  };
  const handleDateMonthChange = (event) => {
    const inputDate = event.target.value ? moment(event.target.value) : moment();
    setFilter({
      ...filter,
      dateMonth: inputDate.format('YYYY-MM'),
      dateDay: inputDate.format('YYYY-MM-DD'),
    });
  };
  const handleDateDayChange = (event) => {
    const inputDate = event.target.value ? moment(event.target.value) : moment();
    dateDayChange(inputDate.format('YYYY-MM-DD'));
  };
  const handleShowGraphClick = () => {
    setShowModalGraph(true);
  };

  const handleCSVDownload = async () => {
    
    const columns = [
      { key: 'date', header: '日付' },
      { key: 'total_billing', header: '売上' },
      { key: 'total_ordergroups', header: 'QR数' },
      { key: 'ordergroups_unit_price', header: '会計単価' },
      { key: 'total_number_of_customers', header: '客数' },
      { key: 'customers_unit_price', header: '客単価' },
      { key: 'total_item_count', header: '商品数' },
      { key: 'payment_method_cash', header: '支払方法（現金）' },
      { key: 'payment_method_others', header: '支払方法（その他）' },
    ];
    const rows = [];
    orderGroupSummany.map((item) => {
      const dateData =
        filter.tableType == 'daily' ? item.date.format('M/D(dd)') : item.date.format('H時');
      const totalBillingData = '¥' + item.totalBilling.toLocaleString();
      const totalOrdergroupsData = item.totalOrdergroups.toLocaleString();
      const orderGroupsUnitPriceData = '¥' + item.ordergroupsUnitPrice.toLocaleString();
      const totalNumberOfCustomerData = item.totalNumberOfCustomers.toLocaleString();
      const customerUnitPriceData = '¥' + item.customersUnitPrice.toLocaleString();
      const totalItemCountData = item.totalItemCount.toLocaleString();
      const paymentMethodCashData = '¥' + item.paymentMethodCash.toLocaleString();
      const paymentMethodOthersData = '¥' + item.paymentMethodOthers.toLocaleString();

      const row = {
        date: dateData,
        total_billing: totalBillingData,
        total_ordergroups: totalOrdergroupsData,
        ordergroups_unit_price: orderGroupsUnitPriceData,
        total_number_of_customers: totalNumberOfCustomerData,
        customers_unit_price: customerUnitPriceData,
        total_item_count: totalItemCountData,
        payment_method_cash: paymentMethodCashData,
        payment_method_others: paymentMethodOthersData,
      };
      rows.push(row);
    });
    const fileName =
      filter.tableType == 'daily'
        ? moment(filter.dateMonth).format('YYYY-MM')
        : moment(filter.dateDay).format('YYYY-MM-DD');

    // generate workbook and add worksheet
    const workbook = new ExcelJS.Workbook();
    workbook.addWorksheet('sheet1');
    const worksheet = workbook.getWorksheet('sheet1');
    worksheet.columns = columns;
    worksheet.addRows(rows);
    const uint8Array = await workbook.csv.writeBuffer({
      formatterOptions: {
        writeBOM: true,
      },
    });

    // generate blob url and download
    const blob = new Blob([uint8Array], { type: 'application/octet-binary' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName + '.csv';
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <PageContainer padding="0px">
      <HeaderAppBar title="注文履歴" />
      <PageInnerContainer padding={'8px 16px'}>
        <Box className={classes.headerActions}>
          <Box className={classes.headerSelectBox}>
            <Box className={classes.selectBox} style={{ width: '100px' }}>
              <CustomSelectorBase
                id="tableType"
                className={classes.select}
                value={filter.tableType}
                optionArray={tableTypes}
                onChange={(event) => handleTableTypeChange(event)}
              />
            </Box>
            <Box className={classes.inputTimeBox}>
              <IconButton
                component="label"
                size="small"
                disabled={disablePreviousButton()}
                onClick={() => filterDateStep(true)}
              >
                <ArrowLeft fontSize="large" />
              </IconButton>
              <Input
                id="dateMonth"
                type="month"
                inputProps={{
                  min: minDate.format('YYYY-MM'),
                  max: maxDate.format('YYYY-MM'),
                }}
                value={filter.dateMonth}
                disableUnderline
                classes={{ root: classes.rootDate }}
                style={{
                  display: filter.tableType === 'daily' ? 'flex' : 'none',
                  margin: 0,
                  width: '140px',
                }}
                onChange={(event) => handleDateMonthChange(event)}
              />
              <Input
                id="dateDay"
                type="date"
                inputProps={{
                  min: minDate.format('YYYY-MM-DD'),
                  max: maxDate.format('YYYY-MM-DD'),
                }}
                value={filter.dateDay}
                disableUnderline
                classes={{ root: classes.rootDate }}
                style={{
                  display: filter.tableType === 'hourly' ? 'flex' : 'none',
                  margin: 0,
                  width: '134px',
                }}
                onChange={(event) => handleDateDayChange(event)}
              />
              <IconButton
                component="label"
                size="small"
                disabled={disableNextButton()}
                onClick={() => filterDateStep()}
              >
                <ArrowRight fontSize="large" />
              </IconButton>
            </Box>
          </Box>
          <Box>
            <Button
              variant="outlined"
              classes={{ root: classes.button }}
              style={{ marginRight: '8px' }}
              onClick={() => handleShowGraphClick()}
            >
              グラフ確認
            </Button>
            <Button
              variant="outlined"
              classes={{ root: classes.button }}
              onClick={() => handleCSVDownload()}
            >
              CSVダウンロード
            </Button>
          </Box>
        </Box>
        <TableContainer className={classes.tableContainer} component={Paper}>
          <Table className={classes.table} stickyHeader aria-label="sticky table" size="small">
            <TableHead>
              <TableRow classes={{ root: classes.tableHead }}>
                {tableHeadCells.map((cell) => (
                  <TableCell
                    key={cell.id}
                    classes={{ root: classes.tableHeadCell }}
                    align="center"
                    colSpan={cell.colSpan}
                    style={{
                      display:
                        filter.tableType === 'hourly' && cell.id === 'detail'
                          ? 'none'
                          : 'table-cell',
                    }}
                  >
                    <Box>{cell.label}</Box>
                    {cell.children?.map((child, index) => (
                      <Box
                        key={index}
                        style={{
                          display: 'inline-block',
                          width: `${100 / cell.children.length}%`,
                        }}
                      >
                        {child.label}
                      </Box>
                    ))}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {orderGroupSummany.map((item, index) => (
                <TableRow
                  key={index}
                  hover
                  className={
                    item.isToday && filter.tableType == 'daily'
                      ? classes.tableRowToday
                      : classes.tableRow
                  }
                >
                  <TableCell classes={{ root: classes.tableCell }} align="center">
                    {filter.tableType === 'daily' ? (
                      <Box
                        className={
                          item.isHoliday
                            ? classes.tableCellHoliday
                            : item.isSaturday
                            ? classes.tableCellSaturday
                            : classes.tableCellWeekDay
                        }
                        onClick={() => dateDayChange(item.date.format('YYYY-MM-DD'), 'hourly')}
                      >
                        {item.date.format('M/D(dd)')}
                      </Box>
                    ) : (
                      <>{item.date.format('H時')}</>
                    )}
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCell }} align="right">
                    &yen;{item.totalBilling.toLocaleString()}
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCell }} align="right">
                    {item.totalOrdergroups.toLocaleString()}
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCell }} align="right">
                    &yen;{item.ordergroupsUnitPrice.toLocaleString()}
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCell }} align="right">
                    {item.totalNumberOfCustomers.toLocaleString()}
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCell }} align="right">
                    &yen;{item.customersUnitPrice.toLocaleString()}
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCell }} align="right">
                    {item.totalItemCount.toLocaleString()}
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCell }} align="right">
                    &yen;{item.paymentMethodCash.toLocaleString()}
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCell }} align="right">
                    &yen;{item.paymentMethodOthers.toLocaleString()}
                  </TableCell>
                  <TableCell
                    classes={{ root: classes.tableCell }}
                    align="center"
                    style={{
                      display: filter.tableType === 'hourly' ? 'none' : 'table-cell',
                    }}
                  >
                    <Button
                      variant="outlined"
                      classes={{ root: classes.buttonDetail }}
                      onClick={() => {
                        history.push(
                          `/order/list/detail?timeStart=${item.date.format(
                            'YYYYMMDD'
                          )}&timeEnd=${item.date.format('YYYYMMDD')}`
                        );
                      }}
                    >
                      表示
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {showModalGraph && (
          <ModalSummaryGraph
            graphData={orderGroupSummany}
            tableType={filter.tableType}
            open={showModalGraph}
            onClose={() => setShowModalGraph(false)}
          />
        )}
        <Waiting isOpen={waiting} />
        <FlashMessage
          isOpen={toast.isShow}
          onClose={(isOpen) => setToast({ ...toast, isShow: isOpen })}
          status={toast.status}
          message={toast.message}
        />
      </PageInnerContainer>
    </PageContainer>
  );
};

PageOrderHistory.propTypes = {};
export default PageOrderHistory;
